/**
 * Whitespace-tolerant text matching helpers.
 *
 * Rendered article text differs from raw markdown source by whitespace
 * (soft line breaks, indentation). These helpers let us find a phrase from
 * rendered text back in the source — and vice versa — by comparing
 * whitespace-collapsed forms while keeping a map back to original offsets.
 */

/** Collapse whitespace runs to single spaces, keeping an index map into the original string. */
export function normalizeWithIndexMap(source: string): { normalized: string; indexMap: number[] } {
  let normalized = '';
  const indexMap: number[] = [];
  let inWhitespace = true; // skips leading whitespace

  for (let i = 0; i < source.length; i++) {
    const char = source[i];
    if (/\s/.test(char)) {
      if (!inWhitespace) {
        normalized += ' ';
        indexMap.push(i);
        inWhitespace = true;
      }
    } else {
      normalized += char;
      indexMap.push(i);
      inWhitespace = false;
    }
  }

  // Drop a trailing collapsed space so end-mapping stays aligned.
  if (normalized.endsWith(' ')) {
    normalized = normalized.slice(0, -1);
    indexMap.pop();
  }

  return { normalized, indexMap };
}

/** Collapse all whitespace runs and trim — the canonical phrase form. */
export function normalizePhrase(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Find `needle` in `haystack`, tolerating whitespace differences.
 * Returns offsets in the original haystack, or null.
 */
export function findNormalized(haystack: string, needle: string): { start: number; end: number } | null {
  const target = normalizePhrase(needle);
  if (!target) return null;

  const { normalized, indexMap } = normalizeWithIndexMap(haystack);
  const index = normalized.indexOf(target);
  if (index === -1) return null;

  const start = indexMap[index];
  const end = indexMap[index + target.length - 1] + 1;
  return { start, end };
}

export interface MappedQuote {
  /** The quote as it appears in the source (original whitespace). */
  quote: string;
  /** The paragraph surrounding the quote, when it could be located. */
  context?: string;
}

const MAX_CONTEXT_LENGTH = 800;

/** Extract the blank-line-delimited paragraph containing [start, end), capped in length. */
export function extractContext(source: string, start: number, end: number): string {
  const paragraphStart = source.lastIndexOf('\n\n', start);
  const paragraphEnd = source.indexOf('\n\n', end);

  const from = paragraphStart === -1 ? 0 : paragraphStart + 2;
  const to = paragraphEnd === -1 ? source.length : paragraphEnd;

  const paragraph = source.slice(from, to).trim();
  if (paragraph.length <= MAX_CONTEXT_LENGTH) return paragraph;

  // Center the window on the quote.
  const quoteMiddle = start - from + Math.floor((end - start) / 2);
  let windowStart = Math.max(from, quoteMiddle - Math.floor(MAX_CONTEXT_LENGTH / 2));
  const windowEnd = Math.min(to, windowStart + MAX_CONTEXT_LENGTH);
  windowStart = Math.max(from, windowEnd - MAX_CONTEXT_LENGTH);

  return (
    (windowStart > from ? '… ' : '') +
    source.slice(windowStart, windowEnd).trim() +
    (windowEnd < to ? ' …' : '')
  );
}

/**
 * Map a DOM text selection back to the source document it was rendered from.
 *
 * Returns the quote in source form (so other clients matching against the
 * raw event content can find it) plus the surrounding paragraph as context.
 * Falls back to the normalized selection when no source match exists (e.g.
 * the selection spans markdown formatting) — still a valid highlight.
 */
export function mapSelectionToQuote(rawSelection: string, source: string): MappedQuote {
  const normalizedSelection = normalizePhrase(rawSelection);
  if (normalizedSelection.length < 2) {
    return { quote: normalizedSelection };
  }

  const match = findNormalized(source, normalizedSelection);
  if (!match) {
    return { quote: normalizedSelection };
  }

  const quote = source.slice(match.start, match.end);
  return {
    quote,
    context: extractContext(source, match.start, match.end),
  };
}
